<template>
  <f-modal
    :class="{ 'is-visible': isVisible }"
    @close="handleClose">
    <f-modal-header>
      <h3 class="heading-medium">
        <template v-if="editMode">
          {{ $t('inquiryFinancialInfo.editSourceIncome') }}
        </template>
        <template v-else>
          {{ $t('inquiryFinancialInfo.addSourceIncome') }}
        </template>
      </h3>
    </f-modal-header>

    <f-modal-body>
      <validation-observer
        ref="inquiry-income-popup-form"
        tag="form">
        <div class="form-group">
          <label for="income-type">{{ $t('inquiryFinancialInfo.typeOfIncome') }}</label>

          <validation-provider
            rules="required"
            name="income-type"
            v-slot="{ errors }">

            <f-form-radio
              v-for="(incomeType, index) in allIncomeTypes"
              :key="index"
              :value="incomeType.id"
              @change="incomeTypeChanged"
              v-model="data.income_type_id">
              {{ incomeType.name }}
            </f-form-radio>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isIncomeOtherNameVisible"
          class="form-group">
          <label for="other-name">{{ $t('inquiryFinancialInfo.nameTypeOfIncome') }}</label>

          <validation-provider
            rules="required"
            name="other-name"
            v-slot="{ errors }">
            <f-input
              v-model="data.income_other_name"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isContractTypeVisible"
          class="form-group">
          <label for="contract-type">{{ $t('inquiryFinancialInfo.formOfContact') }}</label>

          <validation-provider
            rules="required"
            name="contract-type"
            v-slot="{ errors }">

            <f-form-radio
              v-for="(contractType, index) in allContractTypes"
              :key="index"
              :value="contractType.id"
              @change="contractTypeChanged"
              v-model="data.contract_type_id">
              {{ contractType.name }}
            </f-form-radio>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="date-from">{{ $t('adviserSettingsEducationInfo.timeStart') }}</label>
          <validation-provider
            :rules="isDateToVisible ? 'required' : ''"
            name="date-from"
            v-slot="{ errors }">
            <div
              :class="{
                'is-date-range': true,
                'is-invalid': errors.length
              }">
              <v-date-picker
                v-model="data.date_from"
                mode="date"
                :locale="$i18n.locale"
                class="date"
                :max-date='data.date_to'>
                <template v-slot="{ inputValue, togglePopover }">
                  <input
                    :value="inputValue"
                    @click="togglePopover()"
                    class="form-control f-input" />
                </template>
              </v-date-picker>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isDateToVisible"
          class="form-group">
          <label for="date-to">{{ $t('adviserSettingsEducationInfo.timeEnd') }}</label>
          <validation-provider
            rules="required"
            name="date-to"
            v-slot="{ errors }">
            <div
              :class="{
                'is-date-range': true,
                'is-invalid': errors.length
              }">
            <v-date-picker
              v-model="data.date_to"
              mode="date"
              :locale="$i18n.locale"
              class="date"
              :min-date='data.date_from'>
              <template v-slot="{ inputValue, togglePopover }">
                <div class="flex justify-center items-center">
                  <input
                    :value="inputValue"
                    @click="togglePopover()"
                    class="form-control f-input" />
                </div>
              </template>
            </v-date-picker>
            <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isSettlementTypeVisible"
          class="form-group">
          <label for="settlementy-type">{{ $t('inquiryFinancialInfo.settlementMethod') }}</label>

          <validation-provider
            rules="required"
            name="settlementy-type"
            v-slot="{ errors }">

            <f-form-radio
              v-for="(settlementType, index) in allSettlementTypes"
              :key="index"
              :value="settlementType.id"
              @change="settlementTypeChanged"
              v-model="data.settlement_type_id">
              {{ settlementType.name }}
            </f-form-radio>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isRevenueAmountVisible || isIncomeAmountVisible"
          class="form-group">
          <label for="income-currency">{{ $t('inquiryFinancialInfo.currencyOfIncome') }}</label>

          <validation-provider
            rules="required"
            name="income-currency"
            v-slot="{ errors }">
            <div
              :class="{
                'form-select': true,
                'is-invalid': errors.length
              }">
              <v-select
                :options="allCurrencies"
                v-model="data.currency_id"
                :reduce="(currency) => currency.id"
                :clearable="false"
                :searchable="false"
                :placeholder="$t('ui.chooseCurrency')"
                label="name">
              </v-select>
            </div>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isCurrencyOtherNameVisible"
          class="form-group">
          <label for="currency-other-name">{{ $t('inquiryFinancialInfo.nameOfCurrency') }}</label>

          <validation-provider
            name="currency-other-name"
            rules="required"
            v-slot="{ errors }">
            <f-input
              v-model="data.currency_other_name"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isIncomeAmountVisible"
          class="form-group">
          <label for="income-amount">{{ $t('inquiryFinancialInfo.amountOfIncome') }}</label>

          <validation-provider
            rules="required|min_value:1"
            name="income-amount"
            v-slot="{ errors }">
            <f-input-wrapper :append="incomeCurrency">
              <f-input
                v-model="data.income_amount"
                min="0"
                step="1"
                type="number"
                :state="errors.length ? 'invalid' : null"/>
            </f-input-wrapper>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isRevenueAmountVisible"
          class="form-group">
          <label for="revenue-amount-current-year">{{ $t('inquiryFinancialInfo.amountOfRevenueCurrentYear') }}</label>

          <validation-provider
            rules="required|min_value:1"
            name="revenue-amount-current-year"
            v-slot="{ errors }">
            <f-input-wrapper :append="incomeCurrency">
              <f-input
                min="1"
                step="1"
                type="number"
                v-model="data.revenue_amount_current_year"
                :state="errors.length ? 'invalid' : null"/>
            </f-input-wrapper>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isRevenueAmountVisible"
          class="form-group">
          <label for="revenue-amount-last-year">{{ $t('inquiryFinancialInfo.amountOfRevenuePreviousYear') }}</label>

          <validation-provider
            rules=""
            name="revenue-amount-last-year"
            v-slot="{ errors }">
            <f-input-wrapper :append="incomeCurrency">
              <f-input
                min="1"
                step="1"
                type="number"
                v-model="data.revenue_amount_last_year"
                :state="errors.length ? 'invalid' : null"/>
            </f-input-wrapper>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isCopanyIncomeAmountVisible"
          class="form-group">
          <label for="income-amount-current-year">{{ $t('inquiryFinancialInfo.amountOfIncomeCurrentYear') }}</label>

          <validation-provider
            rules="required|min_value:1"
            name="income-amount-current-year"
            v-slot="{ errors }">
            <f-input-wrapper :append="incomeCurrency">
              <f-input
                min="0"
                step="1"
                type="number"
                v-model="data.income_amount_current_year"
                :state="errors.length ? 'invalid' : null"/>
            </f-input-wrapper>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isCopanyIncomeAmountVisible"
          class="form-group">
          <label for="income-amount-last-year">{{ $t('inquiryFinancialInfo.amountOfIncomePreviousYear') }}</label>

          <validation-provider
            rules=""
            name="income-amount-last-year"
            v-slot="{ errors }">
            <f-input-wrapper :append="incomeCurrency">
              <f-input
                v-model="data.income_amount_last_year"
                min="0"
                step="1"
                type="number"
                :state="errors.length ? 'invalid' : null"/>
            </f-input-wrapper>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isRevenueAmountVisible"
          class="form-group">
          <label for="billing-number-current-year">{{ $t('inquiryFinancialInfo.numberBillingMonthsCurrentYear') }}</label>

          <validation-provider
            rules=""
            name="billing-number-current-year"
            v-slot="{ errors }">
            <f-input
              v-model="data.billing_number_current_year"
              min="0"
              step="1"
              type="number"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isTaxRateVisible"
          class="form-group">
          <label for="tax-rate">{{ $t('inquiryFinancialInfo.taxRate') }}</label>

          <validation-provider
            rules="required|min_value:1"
            name="tax-rate"
            v-slot="{ errors }">
            <f-input-wrapper append="%">
              <f-input
                v-model="data.tax_rate"
                min="0"
                step="1"
                type="number"
                :state="errors.length ? 'invalid' : null"/>
            </f-input-wrapper>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div
          v-if="isIndustryVisible"
          class="form-group">
          <label for="industry">{{ $t('inquiryFinancialInfo.industry') }}</label>

          <validation-provider
            name="industry"
            v-slot="{ errors }">
            <div
              :class="{
                'form-select': true,
                'is-invalid': errors.length
              }">
              <v-select
                :options="allIndustries"
                v-model="data.industry_id"
                :reduce="(industry) => industry.id"
                :placeholder="$t('ui.chooseIndustry')"
                label="name">
                <template slot="no-options">
                  {{ $t('ui.noResultsForGivenPhrase') }}
                </template>
              </v-select>
            </div>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="income-bank">{{ $t('inquiryFinancialInfo.bankSalaryReceived') }}</label>

          <validation-provider
            name="income-bank"
            v-slot="{ errors }">
            <div
              :class="{
                'form-select': true,
                'is-invalid': errors.length
              }">
              <v-select
                :options="allBanks"
                v-model="data.bank_id"
                :reduce="(bank) => bank.id"
                :searchable="true"
                :filter="banksSearch"
                :placeholder="$t('ui.chooseBank')"
                label="name">
                <template slot="no-options">
                  {{ $t('ui.noResultsForGivenPhrase') }}
                </template>
              </v-select>
            </div>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="financial-additional-information">{{ $t('inquiryHouseholdInfo.additionalInfo') }}</label>

          <validation-provider
            name="financial-additional-information"
            v-slot="{ errors }">
            <f-textarea
              v-model="data.additional_information"
              :rows="6"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>
      </validation-observer>
    </f-modal-body>
    <f-modal-footer>
      <div class="flex flex-justify-content-between">
        <f-button
          class="ml-0"
          theme="danger"
          @click.prevent="handleClose">
          {{ $t('ui.cancel') }}
        </f-button>
        <f-button
          class="ml-auto"
          theme="primary"
          :disabled="submitInProgress"
          @click.prevent="validateForm">
          <div
            v-if="submitInProgress"
            class="loading-spinner"
            role="status">
          </div>
          <template v-if="editMode">{{ $t('ui.saveChange') }}</template>
          <template v-else>{{ $t('ui.add') }}</template>
        </f-button>
      </div>
    </f-modal-footer>
  </f-modal>
</template>
<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'inquiry-income-popup',
  computed: {
    isIncomeOtherNameVisible () {
      if (this.data.income_type_id) {
        let incomeType = this.allIncomeTypes.find(item => item.id === this.data.income_type_id);
        return incomeType.show_what;
      }

      return false
    },
    isContractTypeVisible () {
      if (this.data.income_type_id) {
        let currentIncomeType = this.allIncomeTypes.find(item => item.id === this.data.income_type_id);
        return !!currentIncomeType.show_contract_type && currentIncomeType.type === 'CONTRACT';
      }

      return false
    },
    isDateToVisible () {
      let showDate = false;

      if (this.data.income_type_id) {
        let currentIncomeType = this.allIncomeTypes.find(item => item.id === this.data.income_type_id);
        showDate = (!currentIncomeType.show_contract_type) && currentIncomeType.type === 'CONTRACT';
      }

      if (!showDate && this.data.contract_type_id) {
        showDate = !!this.allContractTypes.find(item => item.id === this.data.contract_type_id).show_date_to;
      }

      return showDate
    },
    isCurrencyOtherNameVisible () {
      if (this.data.currency_id) {
        let currency = this.allCurrencies.find(item => item.id === this.data.currency_id).code;
        return currency === 'OTHER';
      }

      return false
    },
    isTaxRateVisible () {
      if (this.data.income_type_id && this.data.settlement_type_id) {
        let incomeType = this.allIncomeTypes.find(item => item.id === this.data.income_type_id).type;
        let settlementType = this.allSettlementTypes.find(item => item.id === this.data.settlement_type_id).type;
        return incomeType === 'BUSINESS_ACTIVITY' && (settlementType === 'LUMP_SUM' || settlementType === 'FIXED_AMOUNT_TAX');
      }

      return false
    },
    isIndustryVisible () {
      if (this.data.income_type_id) {
        return this.allIncomeTypes.find(item => item.id === this.data.income_type_id).show_industry;
      }

      return false
    },
    isCopanyIncomeAmountVisible () {
      if (this.data.income_type_id && this.data.settlement_type_id) {
        let incomeType = this.allIncomeTypes.find(item => item.id === this.data.income_type_id).type;
        let settlementType = this.allSettlementTypes.find(item => item.id === this.data.settlement_type_id).type;
        return incomeType === 'BUSINESS_ACTIVITY' && (settlementType === 'LEDGER');
      }

      return false
    },
    isRevenueAmountVisible () {
      if (this.data.income_type_id && this.data.settlement_type_id) {
        let incomeType = this.allIncomeTypes.find(item => item.id === this.data.income_type_id).type;
        let settlementType = this.allSettlementTypes.find(item => item.id === this.data.settlement_type_id).type;
        return incomeType === 'BUSINESS_ACTIVITY' && (settlementType === 'LEDGER' || settlementType === 'LUMP_SUM');
      }

      return false
    },
    isSettlementTypeVisible () {
      if (this.data.income_type_id) {
        return this.allIncomeTypes.find(item => item.id === this.data.income_type_id).type === 'BUSINESS_ACTIVITY';
      }

      return false
    },
    isIncomeAmountVisible () {
      if (this.data.income_type_id) {
        let incomeType = this.allIncomeTypes.find(item => item.id === this.data.income_type_id).type;
        return incomeType === 'CONTRACT' || incomeType === 'OTHER';
      }

      return false
    },
    incomeCurrency () {
      if (this.data.currency_id) {
        if (this.isCurrencyOtherNameVisible) {
          return this.data.currency_other_name;
        }

        return this.allCurrencies.find(item => item.id === this.data.currency_id).code;
      }

      let defaultCurrency = this.allCurrencies.find(item => item.code === 'PLN');

      if (defaultCurrency) {
        return defaultCurrency.code;
      }

      return this.allCurrencies[0].code;
    }
  },
  data() {
    return {
      data: {
        id: 0,
        income_type_id: null,
        income_other_name: null,
        contract_type_id: null,
        date_from: null,
        date_to: null,
        settlement_type_id: null,
        income_amount: 0,
        currency_id: null,
        currency_other_name: '',
        revenue_amount_last_year: 0,
        revenue_amount_current_year: 0,
        income_amount_last_year: 0,
        income_amount_current_year: 0,
        billing_number_current_year: 0,
        tax_rate: 0,
        liabilities: [],
        industry_id: null,
        bank_id: null,
        additional_information: '',
      },
      editMode: false,
      isVisible: false,
      submitInProgress: false,
      originalIncome: null,
      allIncomeTypes: [],
      allContractTypes: [],
      allIndustries: [],
      allBanks: [],
      allLiabilities: [],
      allCurrencies: [],
    };
  },
  mounted() {
    this.$bus.$on('inquiry-income-popup-show', this.openPopup);
  },
  methods: {
    handleClose() {
      document.body.classList.remove('no-scroll');
      this.isVisible = false;
    },
    openPopup(income = null, allIncomeTypes = [], allContractTypes = [], allSettlementTypes = [], allCurrencies = [], allIndustries = [], allBanks = [], allLiabilities = []) {
      this.originalIncome = income;
      this.editMode = this.originalIncome;
      this.allIncomeTypes = allIncomeTypes;
      this.allContractTypes = allContractTypes;
      this.allSettlementTypes = allSettlementTypes;
      this.allCurrencies = allCurrencies;
      this.allIndustries = allIndustries;
      this.allBanks = allBanks;
      this.allLiabilities = allLiabilities;

      if (this.editMode) {
        this.data.id = income.id;
        this.data.income_type_id = income.income_type_id;
        this.data.income_other_name = income.income_other_name;
        this.data.contract_type_id = income.contract_type_id;
        this.data.date_from = null;
        this.data.date_to = null;
        this.data.settlement_type_id = income.settlement_type_id;
        this.data.income_amount = income.income_amount;
        this.data.currency_id = income.currency_id;
        this.data.currency_other_name = income.currency_other_name;
        this.data.revenue_amount_last_year = income.revenue_amount_last_year;
        this.data.revenue_amount_current_year = income.revenue_amount_current_year;
        this.data.income_amount_last_year = income.income_amount_last_year;
        this.data.income_amount_current_year = income.income_amount_current_year;
        this.data.billing_number_current_year = income.billing_number_current_year;
        this.data.tax_rate = income.tax_rate;
        this.data.liabilities = income.liabilities;
        this.data.industry_id = income.industry_id;
        this.data.bank_id = income.bank_id;
        this.data.additional_information = income.additional_information;

        if (!this.data.contract_type_id) {
          this.data.contract_type_id = null;
        }

        if (!this.data.currency_id) {
          this.data.currency_id = null;
        }

        if (!this.data.settlement_type_id) {
          this.data.settlement_type_id = null;
        }
        if (income.date_from) {
          Vue.set(this.data, 'date_from', new Date(income.date_from));
        }
        if (income.date_to) {
          Vue.set(this.data, 'date_to', new Date(income.date_to));
        }
      } else {
        this.data.id = 0;
        this.data.income_type_id = null;
        this.data.income_other_name = null;
        this.data.contract_type_id = null;
        this.data.date_from = null;
        this.data.date_to = null;
        this.data.settlement_type_id = null;
        this.data.income_amount = 0;
        this.data.currency_id = null;
        this.data.currency_other_name = '';
        this.data.revenue_amount_last_year = 0;
        this.data.revenue_amount_current_year = 0;
        this.data.income_amount_last_year = 0;
        this.data.income_amount_current_year = 0;
        this.data.billing_number_current_year = 0;
        this.data.tax_rate = 0;
        this.data.liabilities = [];
        this.data.industry_id = null;
        this.data.bank_id = null;
        this.data.additional_information = '';
      }

      setTimeout(() => {
        this.isVisible = true;
        this.submitInProgress = false;
        document.body.classList.add('no-scroll');
        if (this.$refs['inquiry-income-popup-form']) {
          this.$refs['inquiry-income-popup-form'].reset();
        }
      }, 0);
    },
    validateForm () {
      FormUtils.validate(this.$refs['inquiry-income-popup-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      if (this.originalIncome) {
        Object.assign(this.originalIncome, formDataToSend);
      }
      this.$bus.$emit('inquiry-income-popup-submit', formDataToSend, this.editMode);
      this.handleClose();
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        income_type_id: this.data.income_type_id,
        income_other_name: this.data.income_other_name,
        settlement_type_id: this.data.settlement_type_id,
        contract_type_id: this.data.contract_type_id,
        income_amount: this.data.income_amount,
        currency_id: this.data.currency_id,
        currency_other_name: this.data.currency_other_name,
        revenue_amount_last_year: this.data.revenue_amount_last_year,
        revenue_amount_current_year: this.data.revenue_amount_current_year,
        income_amount_last_year: this.data.income_amount_last_year,
        income_amount_current_year: this.data.income_amount_current_year,
        billing_number_current_year: this.data.billing_number_current_year,
        tax_rate: this.data.tax_rate,
        liabilities: [],
        additional_information: this.data.additional_information
      };

      if (this.id) {
        formDataToSend.id = this.id;
      }

      if (!formDataToSend.contract_type_id) {
        formDataToSend.contract_type_id = 0;
      }

      if (!formDataToSend.currency_id) {
        formDataToSend.currency_id = 0;
      }

      if (!formDataToSend.settlement_type_id) {
        formDataToSend.settlement_type_id = 0;
      }

      if (this.data.bank_id) {
        formDataToSend.bank_id = this.data.bank_id;
      }

      if (this.data.industry_id) {
        formDataToSend.industry_id = this.data.industry_id;
      }

      if (this.data.date_from) {
        formDataToSend.date_from = this.prepareDateString(this.data.date_from);
      }

      if (this.data.date_to) {
        formDataToSend.date_to = this.prepareDateString(this.data.date_to);
      }

      return formDataToSend;
    },
    incomeTypeChanged () {
      this.data.income_other_name = '';
      this.data.contract_type_id = null;
      this.data.settlement_type_id = null;
      this.data.income_amount = 0;
      this.data.currency_id = null;
      this.data.currency_other_name = '';
      this.data.tax_rate = 0;
      this.data.industry_id = null;

      this.settlementTypeChanged();
      this.contractTypeChanged();
    },
    settlementTypeChanged () {
      this.data.income_amount = 0;
      this.data.revenue_amount_last_year = 0;
      this.data.revenue_amount_current_year = 0;
      this.data.income_amount_last_year = 0;
      this.data.income_amount_current_year = 0;
      this.data.billing_number_current_year = 0;
      this.data.tax_rate = 0;
    },
    contractTypeChanged () {
      this.data.date_to = null;
    },
    prepareDateString (input) {
      if (input === null) {
        return this.$t('ui.chooseDate');
      }
      input = new Date(input);

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    },
    dateFormat (classes) {
      return classes
    },
    getLiabilityName (liabilityID) {
      if (!liabilityID) {
        return this.$t('ui.none')
      }

      return this.allLiabilities.find(item => item.id === liabilityID).name;
    },
    banksSearch(options, search) {
      return options.filter(item => (item.name).toLocaleLowerCase('pl').indexOf(search.toLocaleLowerCase('pl')) > -1);
    },
  },
  beforeDestroy () {
    this.$bus.$off('inquiry-income-popup-show', this.openPopup);
  }
}
</script>
